import { TikTokEventType } from 'lib/tracking/tiktok'

export interface HeaderSlice {
    slice_type: SliceTypes.Header
    primary: {
        headline: string
        text: string
        align_bottom: boolean
        background_color: string
        fullwidth: boolean
        is_dark_theme: boolean
        primary_button_color: string
        primary_button_text: string
        primary_button_link: string
        primary_button_click_facebook_event: string
        primary_button_click_tiktok_event: TikTokEventType
        secondary_button_text: string
        secondary_button_link: string
        image_webp: PrismicImage
        image: PrismicImage
        mobile_image: PrismicImage
        primary_button_click_analytics_event_value: string
        landingpage_header_font: boolean
        bottom_svg: PrismicImage
        center_mobile_image: boolean
        button_stempel: PrismicImage
        show_download_buttons?: boolean
    }
    items: {
        advantage: string
    }[]
}

export interface HeaderVideoSlice {
    slice_type: SliceTypes.HeaderVideo
    primary: {
        title: string
        text: string
        video_source: string
        background_color: string
        text_color: string
        video_frame: PrismicImage
        primary_button_color: string
        primary_button_text: string
        primary_button_link: string
        primary_button_click_facebook_event: string
        primary_button_click_tiktok_event: TikTokEventType
        secondary_button_text: string
        secondary_button_link: string
        primary_button_click_analytics_event_value: string
        landingpage_header_font: boolean
        is_standard_component: boolean
        button_stempel: PrismicImage
    }
    items: {
        advantage: string
    }[]
}

export interface FAQSlice {
    slice_type: SliceTypes.FAQ
    primary: {
        background_color: string
        title: string
        title_color: string
        subtitle: string
    }
    items: {
        question: string
        answer: string
    }[]
}

export interface ReferencesSlice {
    slice_type: SliceTypes.References
    primary: {
        background_color: string
        hide_title: boolean
        show_white_logos: boolean
    }
}

export interface GridSlice {
    slice_type: SliceTypes.Grid
    items: {
        title: string
        subtitle: string
    }[]
    primary: {
        title: string
        subtitle: string
        button_link: string
        buttontext: string
    }
}

export interface AdvantageSlice {
    slice_type: SliceTypes.Advantages
    items: {
        title: string
        subtitle: string
    }[]
    primary: {
        background_color: string
        title: string
        title_color: string
        button_color: string
        subtitle: string
        button_link: string
        buttontext: string
        background_image: PrismicImage
    }
}

export interface BlogPostsSlice {
    slice_type: SliceTypes.BlogPosts
    items: {
        article_slug: string
    }[]
    primary: {
        background_color: string
        title: string
        title_color: string
        subtitle: string
        button_link: string
        button_text: string
    }
}

export interface ContactPersonSlice {
    slice_type: SliceTypes.ContactPerson
    primary: {
        heading_text: string
        name: string
        email: string
        button_text: string
        background_color: string
        subtitle: string
        employee_title: string
        white_button: boolean
        image: PrismicImage
        phone_number: string
    }
}

export interface CarouselSlice {
    slice_type: SliceTypes.Carousel
    items: {
        image_webp: PrismicImage
        text: string
        footer_title: string
        footer_subtitle: string
    }[]
    primary: {
        background_color: string
        title_color: string
        title: string
        subtitle: string
    }
}

export interface TestimonialCarouseSlice {
    slice_type: SliceTypes.TestimonialCarousel
    items: {
        image: PrismicImage
        text: string
        user_title: string
        user_subtitle: string
    }[]
    primary: {
        background_color: string
        title: string
        subtitle: string
        subtitle_color: string
        title_color: string
        button_link: string
        button_text: string
    }
}

export interface VideoCarouseSlice {
    slice_type: SliceTypes.VideoCarousel
    items: {
        image: PrismicImage
        video_link: string
        user_title: string
        user_subtitle: string
    }[]
    primary: {
        background_color: string
        title_color: string
        title: string
        subtitle: string
    }
}

export type StatisticsGridSlice = {
    slice_type: SliceTypes.StatisticsGrid
    primary: {
        text_color: string
        background_color: string
        image: PrismicImage
        image_link: string
        title_1: string
        title_2: string
        title_3: string
        title_4: string
        subtitle_1: string
        subtitle_2: string
        subtitle_3: string
        subtitle_4: string
    }
}

export interface BenefitColumnSlice {
    slice_type: SliceTypes.BenefitColumn
    items: {
        title: string
        subtitle: string
    }[]
    primary: {
        background_color: string
        title: string
        subtitle: string
        subtitle_color: string
        text: string
        button_text: string
        button_link: string
        button_click_facebook_event: string
        button_click_tiktok_event: TikTokEventType
        button_color: string
        bottom_color: string
        top_color: string
        text_color: string
        title_color: string
    }
}

export interface ReviewsNumbersSlice {
    slice_type: SliceTypes.ReviewsNumbersSlice
    primary: {
        title: string
        top_text: string
        bottom_text: string
        top_color: string
        bottom_color: string
    }
}

export interface ReviewsSectionSlice {
    slice_type: SliceTypes.ReviewsSection
    items: {
        text: string
        user: string
    }[]
    primary: {
        white_text: boolean
        background_color: string
        title: string
        title_color: string
        subtitle: string
        text: string
    }
}

export interface SVGGridSlice {
    slice_type: SliceTypes.SVGGrid
    items: {
        image: PrismicImage
    }[]
    primary: {
        title: string
        title_color: string
        subtitle: string
        background_color: string
    }
}

export interface InformationListSlice {
    slice_type: SliceTypes.InformationList
    items: {
        title: string
        subtitle: string
    }[]
    primary: {
        background_color: string
        title: string
        title_color: string
        subtitle: string
        center: boolean
        row_align: boolean
    }
}

export interface UserQuoteSlice {
    slice_type: SliceTypes.UserQuote
    primary: {
        background_color: string
        title: string
        title_color: string
        user_quote: string
        user_title: string
        subtitle: string
        button_text: string
        button_link: string
    }
}

export interface CompanyOfferSlice {
    slice_type: SliceTypes.CompanyOffer
    primary: {
        background_color: string
        title: string
        subtitle: string
        title_color: string
        button_text: string
        button_link: string
    }
    items: {
        text: string
        icon: PrismicImage
    }[]
}

export interface FullPageImageSlice {
    slice_type: SliceTypes.FullPageImage
    primary: {
        background_color: string
        image_webp: PrismicImage
        mobile_image: PrismicImage
        text: string
        title: string
    }
    items: {
        text: string
        user: string
    }[]
}

export interface ExpansionBannerSlice {
    slice_type: SliceTypes.ExpansionBanner
    primary: {
        amount_label: string
        text: string
        background_color: string
        placeholder_background_color: string
        svg: PrismicImage
    }
}

export interface FloatingBannerSlice {
    slice_type: SliceTypes.FloatingBanner
    primary: {
        background_color: string
        title: string
        primary_button_text: string
        primary_button_color: string
        primary_button_link: string
        secondary_button_text: string
        secondary_button_link: string
        whatsapp_banner_present: boolean
    }
}

export interface FeatureGridSlice {
    slice_type: SliceTypes.FeatureGrid
    items: {
        imagewebp: PrismicImage
        title: string
        title_color: string
        subtitle: string
        text: string
        button_link: string
        button_text: string
        background_color: string
        button_color: string
        invert_order: boolean
        fullwidth: boolean
        scaling: Scaling
        feature_grid_button_click_analytics_event_value: string
        matching_tab?: string
    }[]
    primary: {
        background_color: string
        title_color: string
        title: string
        subtitle: string
        tabs: string
        secondary_tab_background_color: string
    }
}

export interface TextTestimonialSlice {
    slice_type: SliceTypes.TextTestimonial
    primary: {
        background_color: string
        user_title: string
        user_subtitle: string
        text: string
        subtitle: string
        background_image: PrismicImage
        company_logo: PrismicImage
        footer_info: string
    }
}

export interface TextTestimonialCarouselSlice {
    slice_type: SliceTypes.TextTestimonialCarousel
    items: {
        user_title: string
        user_subtitle: string
        text: string
        subtitle: string
        company_logo: PrismicImage
        footer_info: string
    }[]
    primary: {
        background_color: string
        background_image: PrismicImage
    }
}

export interface ExpansionAnimationSlice {
    slice_type: SliceTypes.ExpansionAnimation
    primary: {
        background_color: string
        text: string
        title: string
        image_webp: PrismicImage
    }
    items: {
        text: string
        user: string
    }[]
}

export interface BottomDownloadBannerSlice {
    slice_type: SliceTypes.BottomDownloadBanner
    primary: {
        text: string
    }
}

export interface PressArticlesShowcaseSlice {
    slice_type: SliceTypes.PressArticlesShowcase
    primary: {
        title: string
        text: string
        subtitle: string
    }
    items: {
        image: PrismicImage
        title: string
        text: string
        link: PrismicLink
    }[]
}

export interface SideFeatureGridSlice {
    slice_type: SliceTypes.SideFeartureGrid
    items: {
        imagewebp: PrismicImage
        title: string
        title_color: string
        subtitle: string
        text: string
        button_link: string
        button_text: string
        background_color: string
        button_color: string
        invert_order: boolean
        fullwidth: boolean
        scaling: Scaling
        feature_grid_button_click_analytics_event_value: string
    }[]
    primary: {
        background_color: string
        title_color: string
        title: string
        subtitle: string
    }
}

export enum SliceTypes {
    Header = 'header',
    HeaderVideo = 'header_video',
    FAQ = 'faq',
    References = 'references',
    Grid = 'grid',
    Advantages = 'grid_1',
    ContactPerson = 'contact_person',
    Carousel = 'carousel',
    StatisticsGrid = 'statistics_grid',
    TestimonialCarousel = 'testimonial_carousel',
    BenefitColumn = 'benefits_columns',
    ReviewsSection = 'reviews',
    InformationList = 'information_list',
    ReviewsNumbersSlice = 'reviews_numbers',
    FeatureGrid = 'feature_grid',
    BlogPosts = 'blog_posts',
    SVGGrid = 'svg_grid',
    TextTestimonial = 'text_testimonial',
    TextTestimonialCarousel = 'text_testimonial_carousel',
    UserQuote = 'userquote',
    VideoCarousel = 'video_carousel',
    CompanyOffer = 'company_offer',
    FloatingBanner = 'floating_banner',
    FullPageImage = 'fullpage_image',
    ExpansionBanner = 'expansion_banner',
    ExpansionAnimation = 'expansionanimation',
    BottomDownloadBanner = 'bottom_download_banner',
    PressArticlesShowcase = 'press_articles_showcase',
    SideFeartureGrid = 'side_fearture_grid',
}

export type PrismicImage = {
    url: string
    alt: string
    dimensions: {
        width: number
        height: number
    }
}

export type PrismicLink = {
    linkType: string
    url: string
}

export type Scaling = 'fill' | 'cover' | 'contain' | 'scale-down'

export type SliceType =
    | HeaderSlice
    | FAQSlice
    | ReferencesSlice
    | GridSlice
    | AdvantageSlice
    | ContactPersonSlice
    | CarouselSlice
    | StatisticsGridSlice
    | TestimonialCarouseSlice
    | BenefitColumnSlice
    | ReviewsSectionSlice
    | InformationListSlice
    | ReviewsNumbersSlice
    | FeatureGridSlice
    | BlogPostsSlice
    | HeaderVideoSlice
    | SVGGridSlice
    | TextTestimonialSlice
    | UserQuoteSlice
    | VideoCarouseSlice
    | CompanyOfferSlice
    | FloatingBannerSlice
    | FullPageImageSlice
    | ExpansionBannerSlice
    | ExpansionAnimationSlice
    | BottomDownloadBannerSlice
    | PressArticlesShowcaseSlice
    | SideFeatureGridSlice
    | TextTestimonialCarouselSlice
